/*  ===========================================================
    src/Pages/YouTubeView.js
    -----------------------------------------------------------
    • Calendar view for YouTube uploads
    • Click "+" on a day to add a YouTube upload
    • Only requires artist name and track name
    • Completely separate from releases/calendar view
    ======================================================== */

import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import emptyArt from '../assets/emptyart.jpg';

/* ───────── helpers ───────── */
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dim = (y, m) => new Date(y, m + 1, 0).getDate();
const first = (y, m) => (new Date(y, m, 1).getDay() + 6) % 7; // 0..6, Monday first

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const YOUTUBE_STATUS_COLORS = {
  'Not Started': '#555',    // gray
  'Rendered': '#9b59b6',    // purple
  'Unlisted': '#f39c12',    // orange
  'Uploaded': '#28a745',     // green
};

/* ► header arrow button */
const Arrow = ({ children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: '#333',
      color: '#fff',
      border: 'none',
      borderRadius: 4,
      width: 32,
      height: 32,
      cursor: 'pointer',
      fontSize: 18,
      lineHeight: '32px',
    }}
  >
    {children}
  </button>
);

/* ► YouTube upload card */
const YouTubeCard = React.memo(
  function YouTubeCard({
    item,
    status,
    onDragStart,
    onClick,
    onToggleStatus,
  }) {
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      } catch {
        return '';
      }
    };

    const coverArtSrc = item.coverArt || emptyArt;
    // Only show release date if it exists (meaning it came from a release)
    const releaseDate = item.releaseDate ? formatDate(item.releaseDate) : null;

    return (
      <div
        draggable
        onDragStart={onDragStart}
        onClick={onClick}
        style={{
          position: 'relative',
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          background: '#2d2d2d',
          borderRadius: 4,
          padding: 10,
          marginTop: 8,
          cursor: 'pointer',
        }}
      >
        <img
          src={coverArtSrc}
          alt=""
          loading="lazy"
          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
        />
        <div
          style={{
            fontSize: 12,
            lineHeight: 1.2,
            color: '#ddd',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden',
            flex: 1,
          }}
        >
          <strong
            style={{
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              display: 'block',
            }}
          >
            {item.trackName}
          </strong>
          <span
            style={{
              color: '#999',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              display: 'block',
              marginTop: 2,
            }}
          >
            {item.artistName}
          </span>
          {releaseDate && (
            <span
              style={{
                color: '#777',
                fontSize: 10,
                marginTop: 2,
              }}
            >
              {releaseDate}
            </span>
          )}
          {status && (
            <div
              onClick={(e) => { 
                e.stopPropagation(); 
                onToggleStatus && onToggleStatus(); 
              }}
              title="Click to change status"
              style={{
                fontSize: 10,
                fontWeight: 'bold',
                background: YOUTUBE_STATUS_COLORS[status] || '#555',
                padding: '2px 6px',
                borderRadius: 4,
                color: '#fff',
                cursor: 'pointer',
                userSelect: 'none',
                opacity: onToggleStatus ? 1 : 0.7,
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
                marginTop: 4,
                alignSelf: 'flex-start',
              }}
            >
              {status}
            </div>
          )}
        </div>
      </div>
    );
  }
);

/* ───────── main component ───────── */
export default function YouTubeView() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  /* month pager */
  const [cur, setCur] = useState(() => {
    try {
      const saved = localStorage.getItem('youtube:last');
      if (saved) {
        const [yy, mm] = saved.split('-').map((n) => parseInt(n, 10));
        if (!Number.isNaN(yy) && !Number.isNaN(mm)) {
          return new Date(yy, mm, 1);
        }
      }
    } catch {}
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  /* month data from server */
  const [days, setDays] = useState({}); // { 'YYYY-MM-DD': [items] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /* status toggle state */
  const [statusBusy, setStatusBusy] = useState({}); // { [uploadId]: true }

  /* modal state */
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState(''); // YYYY-MM-DD
  const [artistName, setArtistName] = useState('');
  const [trackName, setTrackName] = useState('');

  /* edit modal state */
  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editArtistName, setEditArtistName] = useState('');
  const [editTrackName, setEditTrackName] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editStatus, setEditStatus] = useState('Not Started');

  const monthAbort = useRef(null);

  /* fetch month */
  const fetchMonth = async (d) => {
    if (monthAbort.current) monthAbort.current.abort();
    monthAbort.current = new AbortController();
    const { signal } = monthAbort.current;
    setLoading(true);
    setError('');
    const y = d.getFullYear();
    const m = d.getMonth();
    const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    const to = `${y}-${String(m + 1).padStart(2, '0')}-${String(dim(y, m)).padStart(2, '0')}`;
    try {
      const r = await api(`/api/youtube-uploads?from=${from}&to=${to}`, { signal });
      if (!r.ok) throw new Error('API error');
      const data = await r.json(); // { days: { 'YYYY-MM-DD': [items] } }
      setDays(data.days || {});
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonth(cur);
  }, [cur]);

  // Save last-viewed month to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('youtube:last', `${cur.getFullYear()}-${cur.getMonth()}`);
    } catch {}
  }, [cur]);

  /* build month grid */
  const y = cur.getFullYear(),
    m = cur.getMonth();
  const offs = first(y, m);
  const raw = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(y, m, 1 - offs + i);
    d.setHours(0, 0, 0, 0);
    return { date: d, key: ymd(d), inMonth: d.getMonth() === m };
  });
  const cells = raw
    .reduce((rows, c, i) => (rows[Math.floor(i / 7)] ??= []).push(c) && rows, [])
    .filter((r) => r.some((c) => c.inMonth))
    .flat();

  /* drag-n-drop */
  const dragRef = useRef(null);
  const startDrag = (e, item) => {
    dragRef.current = item;
    e.dataTransfer.effectAllowed = 'move';
  };
  const over = (e) => e.preventDefault();

  const drop = async (e, targetDate) => {
    e.preventDefault();
    const drag = dragRef.current;
    if (!drag) return;
    const newISO = ymd(targetDate);

    try {
      await api(`/api/youtube-uploads/${drag.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadDate: newISO }),
      });
      fetchMonth(cur);
    } catch (err) {
      console.error(err);
      alert('Could not update date');
    }
    dragRef.current = null;
  };

  /* add modal helpers */
  const openAddModal = (dateKey) => {
    setAddDate(dateKey);
    setArtistName('');
    setTrackName('');
    setAddOpen(true);
  };

  const handleCreateYouTube = async () => {
    if (!artistName.trim() || !trackName.trim()) {
      alert('Please enter both artist name and track name');
      return;
    }

    try {
      const r = await api('/api/youtube-uploads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artistName: artistName.trim(),
          trackName: trackName.trim(),
          uploadDate: addDate,
        }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`Create failed (${r.status}): ${t}`);
      }
      setAddOpen(false);
      setArtistName('');
      setTrackName('');
      fetchMonth(cur);
    } catch (e) {
      alert(e.message || 'Failed to create YouTube upload');
    }
  };

  /* edit modal helpers */
  const openEditModal = (item) => {
    setEditingItem(item);
    setEditArtistName(item.artistName || '');
    setEditTrackName(item.trackName || '');
    // Format date for input (YYYY-MM-DD)
    const dateStr = item.uploadDate ? new Date(item.uploadDate).toISOString().slice(0, 10) : '';
    setEditDate(dateStr);
    setEditStatus(item.status || 'Not Started');
    setEditOpen(true);
  };

  const handleUpdateYouTube = async () => {
    if (!editArtistName.trim() || !editTrackName.trim() || !editDate) {
      alert('Please fill in all fields');
      return;
    }

    if (!editingItem) return;

    try {
      const r = await api(`/api/youtube-uploads/${editingItem._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artistName: editArtistName.trim(),
          trackName: editTrackName.trim(),
          uploadDate: editDate,
          status: editStatus,
        }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`Update failed (${r.status}): ${t}`);
      }
      setEditOpen(false);
      setEditingItem(null);
      fetchMonth(cur);
    } catch (e) {
      alert(e.message || 'Failed to update YouTube upload');
    }
  };

  const handleDeleteYouTube = async () => {
    if (!editingItem) return;
    if (!window.confirm(`Are you sure you want to delete "${editingItem.trackName}" by ${editingItem.artistName}?`)) {
      return;
    }

    try {
      const r = await api(`/api/youtube-uploads/${editingItem._id}`, {
        method: 'DELETE',
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`Delete failed (${r.status}): ${t}`);
      }
      setEditOpen(false);
      setEditingItem(null);
      fetchMonth(cur);
    } catch (e) {
      alert(e.message || 'Failed to delete YouTube upload');
    }
  };

  /* status toggle */
  const toggleStatus = async (uploadId, dateKey, current) => {
    const id = String(uploadId);
    if (statusBusy[id]) return;

    // Cycle through statuses: Not Started → Rendered → Unlisted → Uploaded → Not Started
    const statusOrder = ['Not Started', 'Rendered', 'Unlisted', 'Uploaded'];
    const currentIndex = statusOrder.indexOf(current || 'Not Started');
    const nextIndex = (currentIndex + 1) % statusOrder.length;
    const next = statusOrder[nextIndex];

    // Optimistic update
    setStatusBusy(b => ({ ...b, [id]: true }));
    setDays(prev => {
      const copy = { ...prev };
      const dayArr = Array.isArray(copy[dateKey]) ? [...copy[dateKey]] : [];
      copy[dateKey] = dayArr.map(item =>
        String(item._id) === id && item.itemType === 'youtube-upload'
          ? { ...item, status: next }
          : item
      );
      return copy;
    });

    try {
      const r = await api(`/api/youtube-uploads/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next }),
      });
      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }
    } catch (e) {
      // Revert on failure
      setDays(prev => {
        const copy = { ...prev };
        const dayArr = Array.isArray(copy[dateKey]) ? [...copy[dateKey]] : [];
        copy[dateKey] = dayArr.map(item =>
          String(item._id) === id && item.itemType === 'youtube-upload'
            ? { ...item, status: current || 'Not Started' }
            : item
        );
        return copy;
      });
      alert('Could not update status.');
    } finally {
      setStatusBusy(b => {
        const c = { ...b };
        delete c[id];
        return c;
      });
    }
  };

  /* month nav */
  const prev = () => setCur((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const next = () => setCur((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  /* ───────── render ───────── */
  return (
    <div style={{ padding: 20 }}>
      {/* CSS for hover reveal of the "+" button */}
      <style>{`
  .day-add-btn { 
    opacity: 0; 
    transition: opacity .15s; 
  }
  .cal-cell:hover .day-add-btn { 
    opacity: 1; 
  }
`}</style>

      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '10px 0 10px',
        }}
      >
        <div>
          <h1 className="header" style={{ margin: 0 }}>
            {MONTHS[m]} {y} - YouTube Uploads
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={() => {
              const now = new Date();
              now.setHours(0, 0, 0, 0);
              setCur(new Date(now.getFullYear(), now.getMonth(), 1));
            }}
            style={{
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              height: 32,
              padding: '0 10px',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
            title="Jump to current month"
          >
            Today
          </button>

          <Arrow onClick={prev}>‹</Arrow>
          <Arrow onClick={next}>›</Arrow>
        </div>
      </header>

      {loading && <p style={{ color: '#999' }}>Loading…</p>}
      {error && <p style={{ color: 'salmon' }}>{error}</p>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7,1fr)',
          gap: 1,
          background: '#333',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            style={{
              background: '#2a2a2a',
              color: '#bbb',
              textAlign: 'center',
              fontWeight: 'bold',
              padding: '6px 0',
              fontSize: 14,
            }}
          >
            {w}
          </div>
        ))}

        {cells.map(({ date, key, inMonth }) => {
          const list = days[key] || [];
          const isToday = date.getTime() === new Date(new Date().setHours(0, 0, 0, 0)).getTime();

          return (
            <div
              key={key}
              className="cal-cell"
              onDragOver={over}
              onDrop={(e) => drop(e, date)}
              style={{
                background: inMonth ? '#1e1e1e' : '#242424',
                minHeight: 120,
                padding: '24px 6px 6px',
                position: 'relative',
                border: isToday ? '2px solid #7eef4f' : '1px solid #2a2a2a',
                overflowY: 'auto',
              }}
            >
              {/* day number */}
              <span
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 8,
                  fontSize: 12,
                  color: inMonth ? '#888' : '#666',
                }}
              >
                {date.getDate()}
              </span>

              {/* add button (hover reveal) */}
              <button
                onClick={() => openAddModal(key)}
                title="Add YouTube upload on this day"
                className="day-add-btn"
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 6,
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: '#2c2c2c',
                  border: '1px solid #3a3a3a',
                  color: '#fff',
                  lineHeight: '20px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  zIndex: 3,
                  transition: 'opacity .15s',
                }}
              >
                +
              </button>

              {list.map((it) => {
                const itDate = new Date(it.uploadDate);
                itDate.setHours(0, 0, 0, 0);
                const dimmed = itDate.getTime() < today.getTime();
                const status = it.status || 'Not Started';

                return (
                  <YouTubeCard
                    key={it._id}
                    item={it}
                    status={status}
                    onDragStart={(e) =>
                      startDrag(e, {
                        id: it._id,
                        oldDate: typeof it.uploadDate === 'string' ? it.uploadDate.slice(0, 10) : '',
                      })
                    }
                    onClick={() => openEditModal(it)}
                    onToggleStatus={() => toggleStatus(it._id, key, status)}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Edit YouTube Upload Modal */}
      {editOpen && editingItem && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setEditOpen(false)}
        >
          <div
            style={{
              background: '#1e1e1e',
              padding: 24,
              borderRadius: 10,
              width: 400,
              maxWidth: '90vw',
              color: 'white',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>Edit YouTube Upload</h2>

            <label style={{ display: 'block', marginBottom: 8 }}>Artist Name</label>
            <input
              type="text"
              value={editArtistName}
              onChange={(e) => setEditArtistName(e.target.value)}
              placeholder="Enter artist name"
              style={{
                width: '100%',
                padding: 10,
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                marginBottom: 16,
                boxSizing: 'border-box',
              }}
              autoFocus
            />

            <label style={{ display: 'block', marginBottom: 8 }}>Track Name</label>
            <input
              type="text"
              value={editTrackName}
              onChange={(e) => setEditTrackName(e.target.value)}
              placeholder="Enter track name"
              style={{
                width: '100%',
                padding: 10,
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                marginBottom: 16,
                boxSizing: 'border-box',
              }}
            />

            <label style={{ display: 'block', marginBottom: 8 }}>Upload Date</label>
            <input
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                marginBottom: 16,
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                fontSize: 'inherit',
              }}
            />

            <label style={{ display: 'block', marginBottom: 8 }}>Status</label>
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                marginBottom: 20,
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                cursor: 'pointer',
              }}
            >
              <option value="Not Started">Not Started</option>
              <option value="Rendered">Rendered</option>
              <option value="Unlisted">Unlisted</option>
              <option value="Uploaded">Uploaded</option>
            </select>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
              <button
                onClick={handleDeleteYouTube}
                style={{
                  padding: '10px 20px',
                  background: '#ea5a54',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 5,
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setEditOpen(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#555',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 5,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateYouTube}
                  style={{
                    padding: '10px 20px',
                    background: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 5,
                    cursor: 'pointer',
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add YouTube Upload Modal */}
      {addOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setAddOpen(false)}
        >
          <div
            style={{
              background: '#1e1e1e',
              padding: 24,
              borderRadius: 10,
              width: 400,
              maxWidth: '90vw',
              color: 'white',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>Add YouTube Upload</h2>
            <p style={{ color: '#999', marginBottom: 20 }}>
              Date: {addDate}
            </p>

            <label style={{ display: 'block', marginBottom: 8 }}>Artist Name</label>
            <input
              type="text"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              placeholder="Enter artist name"
              style={{
                width: '100%',
                padding: 10,
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                marginBottom: 16,
                boxSizing: 'border-box',
              }}
              autoFocus
            />

            <label style={{ display: 'block', marginBottom: 8 }}>Track Name</label>
            <input
              type="text"
              value={trackName}
              onChange={(e) => setTrackName(e.target.value)}
              placeholder="Enter track name"
              style={{
                width: '100%',
                padding: 10,
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                marginBottom: 20,
                boxSizing: 'border-box',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateYouTube();
                }
              }}
            />

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAddOpen(false)}
                style={{
                  padding: '10px 20px',
                  background: '#555',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 5,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateYouTube}
                style={{
                  padding: '10px 20px',
                  background: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 5,
                  cursor: 'pointer',
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

